import { signal } from '@preact/signals';
// The Manager Entry export's data calls (L-26), kept out of the component so
// they can be tested without rendering — the same split managerEntry.ts uses.
//
// Nothing here computes a total or decides what belongs on the report. The
// server builds it (src/manager_entry_report.js) and this module fetches it:
// a second implementation of "everything submitted for this period" is a
// second answer to the question the report exists to settle.
import { authedGet, authedWrite } from './apiClient';
import { API_BASE } from './config';
import { authToken } from './auth';
import { withViewAs } from './viewAs';

export interface ReportMoneyRow {
  id: number;
  submissionId: number;
  employeeId: number;
  employeeName: string | null;
  amount: string | null;
  detail: string | null;
  note: string | null;
  payCode: string | null;
  enteredBy: string;
  enteredAt: string;
  /** Voided lines are ON the report, marked — never dropped (his rule). */
  void: boolean;
  voidedAt: string | null;
  voidedBy: string | null;
}

export interface ReportMoneySection {
  key: string;
  title: string;
  payCode: string;
  kind: 'EARNING' | 'DEDUCTION';
  detailLabel: string;
  rows: ReportMoneyRow[];
  rowCount: number;
  voidedCount: number;
  total: string;
}

/** New-hire-only extra fields (position, address, phone, email, W4 status —
 * server's NEW_HIRE_EXTRA_FIELDS). Keyed by camelCase field key; an unknown
 * key still renders, labeled by the key itself (see the component). */
export type ReportRosterExtra = Record<string, string | null>;

/** Labels for `extra`'s keys, copied from src/manager_entry.js's
 * NEW_HIRE_EXTRA_FIELDS (Code.gs's own field set) — never invented here.
 * A key not in this map still renders, labeled by the key itself, so a
 * future field never goes silently missing from the detail panel. */
export const NEW_HIRE_EXTRA_LABELS: Record<string, string> = {
  dateOfBirth: 'Date of birth',
  position: 'Position',
  streetAddress: 'Street address',
  city: 'City',
  state: 'State',
  zipCode: 'Zip code',
  phone: 'Phone',
  email: 'Email',
  w4Status: 'W4 status',
};

export function extraFieldLabel(key: string): string {
  return NEW_HIRE_EXTRA_LABELS[key] ?? key;
}

export interface ReportRosterRow {
  id: number;
  submissionId: number;
  employeeId: number;
  employeeName: string | null;
  field1: string;
  field2: string;
  note: string | null;
  /** New-hire-only detail (position, address, phone, email, W4 status).
   * Absent (null) for every other change type. */
  extra: ReportRosterExtra | null;
  /** NEW_HIRE only: the person is not on the roster yet, so the manager
   * typed a name instead of picking Who. Null for every other change type
   * (employeeName already covers those). */
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  /** Flags, never values (SPEC-001 §7). */
  ssnProvided: boolean;
  bankProvided: boolean;
  enteredBy: string;
  enteredAt: string;
  void: boolean;
  voidedAt: string | null;
  voidedBy: string | null;
  /** "Mark as entered [in ADP]" (owner, 2026-09-22). Reversible; both null
   * until Tomer marks the row entered. */
  enteredInAdpAt: string | null;
  enteredInAdpBy: string | null;
}

export interface ReportRosterSection {
  key: string;
  title: string;
  changeType: string;
  field1Label: string;
  field2Label: string;
  rows: ReportRosterRow[];
  rowCount: number;
  voidedCount: number;
}

export interface ManagerEntryReport {
  location: { code: string; name: string };
  payPeriod: {
    id: number;
    periodCode: string;
    adpWeek: number | null;
    startDate: string;
    endDate: string;
    closed: boolean;
    label: string;
  };
  submissions: Array<{
    id: number;
    lineCount: number;
    rosterCount: number;
    earningTotal: string | null;
    deductionTotal: string | null;
    submittedBy: string;
    submittedAt: string;
  }>;
  moneySections: ReportMoneySection[];
  rosterSections: ReportRosterSection[];
  unclassified: Array<ReportMoneyRow & { kind: string; category: string }>;
  totals: {
    earnings: string;
    deductions: string;
    lineCount: number;
    activeLineCount: number;
    voidedLineCount: number;
    rosterCount: number;
    voidedRosterCount: number;
  };
  reconciliation: {
    submissionCount: number;
    declaredLineCount: number;
    foundLineCount: number;
    declaredRosterCount: number;
    foundRosterCount: number;
    unclassifiedCount: number;
    /** False means the rows found disagree with what the submissions declared.
     * Shown to the reader rather than swallowed — see the component. */
    complete: boolean;
  };
}

export function reportPath(location: string, payPeriodId: number): string {
  return `/api/v1/payroll/manager-entry/report?location=${encodeURIComponent(location)}`
    + `&payPeriod=${payPeriodId}`;
}

export function loadManagerEntryReport(
  location: string,
  payPeriodId: number,
): Promise<ManagerEntryReport> {
  return authedGet<ManagerEntryReport>(reportPath(location, payPeriodId));
}

export function csvFilename(report: ManagerEntryReport): string {
  return `manager-entry-${report.location.code}-${report.payPeriod.periodCode}.csv`;
}

/**
 * The CSV, fetched with the bearer token and handed to the browser as a file.
 *
 * A plain `<a href>` cannot do this: the endpoint needs an Authorization
 * header, and there is deliberately no unauthenticated URL for payroll data
 * (SPEC-001 §6). So the payroll agent signs in like a person; this button is
 * the same bytes for whoever is at the screen.
 */
export async function fetchReportCsv(location: string, payPeriodId: number): Promise<string> {
  const token = authToken.value;
  const res = await fetch(`${API_BASE}${withViewAs(`${reportPath(location, payPeriodId)}&format=csv`)}`, {
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error || `HTTP ${res.status}`);
  }
  return res.text();
}

/**
 * "Write to payroll folder" (payroll-017, docs/payroll/app-export-now-button.md)
 * — the result shape of src/payroll_export_job.js's runPayrollExport, passed
 * through the route unchanged. `ran: false` is a business "no" (wrong
 * period, no Drive folder registered, not an error) and still comes back
 * with a 200; the component renders `reason` verbatim either way.
 */
export interface PayrollExportFile {
  name: string;
  id: string | null;
  updated: boolean;
}

export interface PayrollExportClinicResult {
  locationCode: string;
  moneyRows: number;
  rosterRows: number;
  ok: boolean;
  reason: string | null;
  files: PayrollExportFile[];
}

export type PayrollExportResult =
  | {
      ran: true;
      payPeriod: { id: string; periodCode: string; adpWeek: number | null };
      week: number | null;
      clinics: PayrollExportClinicResult[];
      held: string[];
      ok: boolean;
    }
  | { ran: false; reason: string };

export interface EnteredInAdpResult {
  rosterChange: { id: number; enteredInAdpAt: string | null; enteredInAdpBy: string | null };
  enteredInAdp: boolean;
  replayed?: boolean;
  version?: number;
}

/**
 * "Mark as entered [in ADP]" (owner, 2026-09-22) — reversible: the same call
 * with `enteredInAdp: false` unmarks it. OWNER/DIRECTOR only; the server
 * enforces the gate too, this just avoids showing the button to anyone else.
 */
export function setRosterEnteredInAdp(
  id: number,
  enteredInAdp: boolean,
  key: string = crypto.randomUUID(),
): Promise<EnteredInAdpResult> {
  return authedWrite<EnteredInAdpResult>(
    'POST', `/api/v1/payroll/manager-entry/roster/${id}/entered-in-adp`, { enteredInAdp }, key,
  );
}

export function postExportNow(payPeriodId: number): Promise<PayrollExportResult> {
  return authedWrite<PayrollExportResult>(
    'POST',
    '/api/v1/payroll/manager-entry/export-now',
    { payPeriodId },
  );
}

/**
 * Owner, 2026-11-03: "i have new hires i cant click their name". A name in the
 * Submitted table asks the Payroll export box to open that person's record:
 * the box opens on the row's week, loads, and shows the same detail panel.
 */
export const rosterDetailRequest = signal<{ payPeriodId: number; rosterId: number } | null>(null);
